import { useState, useEffect, useCallback } from 'react';
import {
    useTheme,
    useMediaQuery,
    CircularProgress,
    Box,
    Typography,
    Button,
    Card,
    CardHeader,
    IconButton,
    Tooltip,
    Checkbox,
    TextField,
    MenuItem,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    ListItemIcon,
    Paper,
} from '@mui/material';
import { Delete, Subscriptions, Unsubscribe } from '@mui/icons-material';
import toast from 'react-hot-toast';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import FlexColumn from 'components/FlexColumn';
import StyledButton from 'components/StyledButton';


function not(a, b) {
    return a.filter((value) => !b.includes(value));
}

function intersection(a, b) {
    return a.filter((value) => b.includes(value));
}

function union(a, b) {
    return [...a, ...not(b, a)];
}

const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};


const NoticeBoard = () => {
    useAuthCheck();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [vapidPublicKey, setVapidPublicKey] = useState(null);
    const [serviceWorkerRegistration, setServiceWorkerRegistration] = useState(null);

    const [checked, setChecked] = useState([]);
    const [left, setLeft] = useState([]);
    const [right, setRight] = useState([]);
    const [leftSearchTerm, setLeftSearchTerm] = useState('');
    const [rightSearchTerm, setRightSearchTerm] = useState('');

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    const [values, setValues] = useState({ to: [], type: '', priority: 1, message: '' });

    const fetchUsers = useCallback(async () => {
        try {
            const res = await api.get('/users/active');
            setLeft(res.data);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to fetch users!');
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        try {
            const response = await api.get('/notifications/allnotifications');
            setNotifications(response.data.data || []);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to fetch notifications!');
        }
    }, []);

    const fetchVapidPublicKey = async () => {
        try {
            const response = await api.get('/notifications/vapidPublicKey');
            setVapidPublicKey(response.data.data.publicKey);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to fetch VAPID public key!');
        }
    };

    const checkSubscription = async () => {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            toast.error('Service Worker not supported!');
            return;
        }
        try {
            const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
            setServiceWorkerRegistration(registration);
            const subscription = await registration.pushManager.getSubscription();
            setIsSubscribed(!!subscription);
        } catch (err) {
            toast.error(`Failed to register service worker: ${err.message}`);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchNotifications();
        fetchVapidPublicKey();
        checkSubscription();
    }, [fetchUsers, fetchNotifications]);

    const handleSubscribe = async () => {
        try {
            setLoading(true);

            if (!('Notification' in window)) {
                toast.error('Push notifications are not supported on this device.');
                return;
            }

            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                toast.error('Notification permission denied. Please allow notifications in your browser/device settings.');
                return;
            }

            const registration = serviceWorkerRegistration || (await navigator.serviceWorker.ready);
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
            });
            await api.post('/notifications/subscribe', subscription);
            setIsSubscribed(true);
            toast.success('Subscribed to notifications.');
        } catch (err) {
            toast.error(err.response?.data?.message || `Subscription failed: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleUnsubscribe = async () => {
        try {
            setLoading(true);
            const registration = serviceWorkerRegistration || (await navigator.serviceWorker.ready);
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                await subscription.unsubscribe();
                await api.delete('/notifications/unsubscribe');
                setIsSubscribed(false);
                toast.success('Unsubscribed from notifications.');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || `Unsubscription failed: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handlePush = async () => {
        try {
            setLoading(true);
            const payload = {
                to: right,
                type: values.type,
                priority: Number(values.priority) || 1,
                message: values.message,
            };
            await api.post('/notifications/push', payload);
            await fetchNotifications();
            toast.success('Push notification sent.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to send push notification');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteNotification = async (id) => {
        try {
            setLoading(true);
            await api.delete(`/notifications/delete/${id}`);
            setNotifications(notifications.filter((n) => n._id !== id));
            toast.success('Notification deleted.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete notification!');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAllNotifications = async () => {
        try {
            setLoading(true);
            await api.delete('/notifications/deleteallofuser');
            setNotifications([]);
            toast.success('All notifications deleted.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete all notifications!');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setValues({ ...values, [e.target.name]: e.target.value });
    };

    const parseNotificationData = (data) => {
        if (!data) return 'No data';
        if (typeof data === 'string') {
            try {
                const parsed = JSON.parse(data);
                return parsed.title || data;
            } catch {
                return data;
            }
        }
        return data.title || String(data);
    };

    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];
        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setChecked(newChecked);
    };

    const numberOfChecked = (items) => intersection(checked, items).length;

    const handleToggleAll = (items) => () => {
        if (numberOfChecked(items) === items.length) {
            setChecked(not(checked, items));
        } else {
            setChecked(union(checked, items));
        }
    };

    const handleCheckedRight = () => {
        setRight(right.concat(leftChecked));
        setLeft(not(left, leftChecked));
        setChecked(not(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
        setLeft(left.concat(rightChecked));
        setRight(not(right, rightChecked));
        setChecked(not(checked, rightChecked));
    };

    const customList = (title, items, searchTerm, onSearchChange) => {
        const filteredItems = items.filter((user) => {
            const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.toLowerCase();
            const reverseFullName = `${user?.lastName || ''} ${user?.firstName || ''}`.toLowerCase();
            const search = searchTerm.toLowerCase().trim();
            return fullName.includes(search) || reverseFullName.includes(search);
        });

        return (
            <Card sx={{ width: '100%' }}>
                <CardHeader
                    sx={{ px: 2, py: 1 }}
                    avatar={
                        <Checkbox
                            onClick={handleToggleAll(filteredItems)}
                            checked={numberOfChecked(filteredItems) === filteredItems.length && filteredItems.length !== 0}
                            indeterminate={
                                numberOfChecked(filteredItems) !== filteredItems.length &&
                                numberOfChecked(filteredItems) !== 0
                            }
                            disabled={filteredItems.length === 0}
                            slotProps={{ 'aria-label': 'all items selected' }}
                        />
                    }
                    title={title}
                    subheader={`${numberOfChecked(filteredItems)}/${filteredItems.length} selected`}
                />
                <Box px={2} pb={1}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={onSearchChange}
                    />
                </Box>
                <Divider />
                <List
                    sx={{ width: '100%', height: 250, bgcolor: 'background.paper', overflow: 'auto' }}
                    dense
                    component="div"
                    role="list"
                >
                    {filteredItems.map((value) => {
                        const labelId = `transfer-list-item-${value._id}-label`;
                        return (
                            <ListItemButton key={value._id} role="listitem" onClick={handleToggle(value)}>
                                <ListItemIcon>
                                    <Checkbox
                                        checked={checked.includes(value)}
                                        tabIndex={-1}
                                        disableRipple
                                        inputProps={{ 'aria-labelledby': labelId }}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    id={labelId}
                                    primary={`${value?.lastName.toUpperCase()} ${value?.firstName?.toUpperCase()}`}
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Card>
        );
    };


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Notice Board"
                    subtitle={loading ? 'Loading...' : 'Push notifications to users'}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title={isSubscribed ? 'Unsubscribe' : 'Subscribe'} arrow>
                            <span>
                                <StyledButton
                                    color={isSubscribed ? 'error' : 'success'}
                                    disabled={loading || !vapidPublicKey}
                                    onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
                                >
                                    {isSubscribed ? <Unsubscribe /> : <Subscriptions />}
                                </StyledButton>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <FlexColumn gap={2} sx={{ gridColumn: "span 12" }}>
                        {/* Transfer List */}
                        <Box
                            display="flex"
                            alignItems={'center'}
                            flexDirection={isDesktop ? 'row' : 'column'}
                            width="100%"
                            gap={2}
                        >
                            <Box sx={{ flex: 1, width: '100%' }}>
                                {customList('Choices', left, leftSearchTerm, (e) => setLeftSearchTerm(e.target.value))}
                            </Box>
                            <Box
                                display="flex"
                                alignItems="center"
                                flexDirection={isDesktop ? 'column' : 'row'}
                                gap={1}
                            >
                                <StyledButton
                                    aria-label="move selected right"
                                    disabled={leftChecked.length === 0}
                                    onClick={handleCheckedRight}
                                    sx={{ minWidth: 36, width: 36, height: 36 }}
                                >
                                    {isDesktop ? '>' : '↓'}
                                </StyledButton>
                                <StyledButton
                                    aria-label="move selected left"
                                    disabled={rightChecked.length === 0}
                                    onClick={handleCheckedLeft}
                                    sx={{ minWidth: 36, width: 36, height: 36 }}
                                >
                                    {isDesktop ? '<' : '↑'}
                                </StyledButton>
                            </Box>
                            <Box sx={{ flex: 1, width: '100%' }}>
                                {customList('Chosen', right, rightSearchTerm, (e) => setRightSearchTerm(e.target.value))}
                            </Box>
                        </Box>

                        {/* Compose Form */}
                        <FlexBetween width="100%" gap={1}>
                            <TextField
                                fullWidth
                                variant="filled"
                                required
                                select
                                label="Type"
                                name="type"
                                size="small"
                                value={values.type}
                                onChange={handleChange}
                            >
                                {['follow', 'comment', 'like', 'alert', 'bookmark', 'admin'].map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {item.charAt(0).toUpperCase() + item.slice(1)}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                fullWidth
                                variant="filled"
                                type="number"
                                label="Priority"
                                name="priority"
                                size="small"
                                value={values.priority}
                                onChange={handleChange}
                                sx={{ '& .MuiFilledInput-input': { textAlign: 'right' } }}
                                slotProps={{ input: { min: 1 } }}
                            />
                        </FlexBetween>

                        <TextField
                            fullWidth
                            variant="filled"
                            required
                            label="Message"
                            name="message"
                            size="small"
                            value={values.message}
                            onChange={handleChange}
                        />

                        <Button
                            aria-label="push notification"
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            sx={{ height: 42, mb: 1 }}
                            onClick={handlePush}
                        >
                            Push
                        </Button>

                        {/* Sent Notifications List */}
                        <Paper elevation={3} sx={{ width: '100%', p: 3 }}>
                            {notifications.length === 0 ? (
                                <Typography color="text.secondary">No notifications found.</Typography>
                            ) : (
                                <List>
                                    <Typography fontWeight="bold" color="text.primary" mb={1}>
                                        {notifications.length === 1
                                            ? '1 notification'
                                            : `${notifications.length} notifications`}
                                    </Typography>
                                    {notifications.map((notification) => (
                                        <ListItem
                                            key={notification._id}
                                            sx={{
                                                bgcolor: notification.isRead ? 'neutral.light' : 'primary.light',
                                                borderRadius: 1,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mb: 1,
                                            }}
                                        >
                                            <ListItemText
                                                primary={parseNotificationData(notification.message)}
                                                secondary={
                                                    <>
                                                        <Typography variant="body2">Type: {notification?.type}</Typography>
                                                        <Typography variant="body2">
                                                            Recipient: {notification?.recipientLastName?.toUpperCase()} {notification?.recipientFirstName?.toUpperCase()}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {new Date(notification.createdAt).toLocaleString()}
                                                        </Typography>
                                                    </>
                                                }
                                            />
                                            <IconButton
                                                aria-label={`Delete notification ${notification._id}`}
                                                edge="end"
                                                color="error"
                                                disabled={loading}
                                                onClick={() => handleDeleteNotification(notification._id)}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                            {notifications.length > 0 && (
                                <Button
                                    aria-label="Delete all"
                                    fullWidth
                                    variant="contained"
                                    color="error"
                                    disabled={loading}
                                    sx={{ mt: 2 }}
                                    onClick={handleDeleteAllNotifications}
                                >
                                    Delete All
                                </Button>
                            )}
                        </Paper>
                    </FlexColumn>
                </Box>
            )}

            <ScrollToTop />
        </Box>
    );
};

export default NoticeBoard;
